import { useExamCenter } from '@exam-center/exam-center-details';
import { FilterOperator, useSearchExamCenterCoursesQuery } from '@exam-center/graphql';
import { DataGrid } from '@shared/components';
import { useMemo } from 'react';

import { ExamCenterCourseListNoRowsOverlay } from '../../components';
import { useExamCenterCourseListSearchParams } from '../../hooks';

import { columns } from './columns';
import { mapExamCenterCourseData } from './mapper';
import { ORDER_TYPE } from './utils';

export default function ExamCenterCourseList() {
  const { examCenter } = useExamCenter();
  const { code: examCenterCode } = examCenter;

  const [{ page, pageSize, sortOrder, sortBy }, { changePageNumber, changePageSize }] =
    useExamCenterCourseListSearchParams();

  const { data: examCenterCoursesData, loading: isLoading } = useSearchExamCenterCoursesQuery({
    variables: {
      searchExamCenterCoursesInput: {
        filter: {
          conditions: [
            {
              field: 'examCenterCode',
              operator: FilterOperator.Equal,
              value: examCenterCode,
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
      },
    },
  });

  const examCenterCourses = useMemo(() => {
    return (
      examCenterCoursesData?.ecSearchExamCenterCourses.examCenterCourses.map(
        mapExamCenterCourseData,
      ) ?? []
    );
  }, [examCenterCoursesData]);

  const totalExamCenterCourses = examCenterCoursesData?.ecSearchExamCenterCourses.total ?? 0;

  return (
    <DataGrid
      columns={columns}
      rows={examCenterCourses}
      hideFooter={!totalExamCenterCourses}
      loading={isLoading}
      slots={{
        noRowsOverlay: ExamCenterCourseListNoRowsOverlay,
      }}
      slotProps={{
        footer: {
          total: totalExamCenterCourses,
          pagination: {
            page,
            count: totalExamCenterCourses,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      disableColumnMenu
      getRowHeight={() => 48}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: '100%',
        '.MuiDataGrid-cell': {
          p: theme.spacing(3),
          alignItems: 'flex-start',
        },
        '.MuiDataGrid-columnHeader': {
          p: theme.spacing(3),
        },
        '.MuiDataGrid-cell[data-field="moreAction"]': {
          padding: theme.spacing(1.5),
        },
        '.MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '--DataGrid-overlayHeight': '404px',
      })}
    />
  );
}
